/**
 * A router, about forty lines of it.
 *
 * The app has two routes. A routing library would be more code than the thing
 * it routes, and every one of them brings its own opinion about where state
 * lives — which this app has already answered: the URL *is* the query
 * (REQ-WEB-002).
 *
 * The only subtle part is the history discipline REQ-WEB-023 requires:
 *
 * * Moving a slider or typing a keystroke **replaces** the current entry.
 *   Dragging must not push thirty entries onto the back stack.
 * * Opening a lens **pushes** one, so Back returns to the list. So does a
 *   finished change to the list (D33): the caller says which with
 *   `replace(to, true)`.
 * * The list's scroll position is not in the URL and would otherwise be lost.
 *   It is stashed in the history entry being left behind, which is the only
 *   place that survives a Back and belongs to that entry alone.
 */
import { useCallback, useEffect, useRef, useState } from "preact/hooks";

export interface Route {
  path: string;
  search: string;
}

interface HistoryState {
  scrollY?: number;
}

function current(): Route {
  return { path: location.pathname, search: location.search };
}

/** `/lens/{slug}` → slug, else null. */
export function lensSlug(path: string): string | null {
  const m = /^\/lens\/([A-Za-z0-9-]+)\/?$/.exec(path);
  return m && m[1] ? m[1] : null;
}

/** Is this the comparison view? */
export function isCompare(path: string): boolean {
  return /^\/compare\/?$/.test(path);
}

/**
 * The shortest gap between two history writes.
 *
 * **WebKit throttles `history.replaceState` to 100 calls per 10 seconds and
 * throws a `SecurityError` on the 101st.** Measured in the Playwright WebKit
 * build: 400 calls in a tight loop, 99 succeeded, 301 threw. Chromium has no
 * such limit, which is why this was invisible here for months.
 *
 * The query is the URL (REQ-WEB-002) and every slider step is a query change,
 * so a single drag used to spend forty of that budget. Past the limit the URL
 * simply stopped following the control: the readout said one thing, the
 * address bar another, a shared link carried a value nobody chose, and the
 * effect that reads the query back from the URL then fought the one writing it.
 *
 * 150 ms holds it to under seven writes a second — a third of WebKit's
 * allowance — and the cost is that a URL copied mid-drag can be up to 150 ms
 * old. The last value is always written: this coalesces, it never drops.
 */
const HISTORY_MIN_GAP_MS = 150;

/** Remember the scroll position on the entry being left, so Back restores it. */
function stashScroll() {
  const state: HistoryState = { ...(history.state as HistoryState | null), scrollY: scrollY };
  history.replaceState(state, "", location.pathname + location.search);
}

export function useRoute() {
  const [route, setRoute] = useState<Route>(current);

  useEffect(() => {
    const onPop = () => setRoute(current());
    addEventListener("popstate", onPop);
    return () => removeEventListener("popstate", onPop);
  }, []);

  const pending = useRef<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastWrite = useRef(0);

  /**
   * The last URL this hook wrote itself.
   *
   * The query is written to the URL and read back from it, so something has to
   * tell the two apart or they fight. The caller used to remember what it had
   * *asked* for, which stopped being the same thing once writes were coalesced:
   * a queued write lands carrying an older query than the one the caller last
   * asked for, the caller does not recognise it, and reads it back over the
   * newer one. That is a value moving backwards under the user's finger.
   *
   * What arrives is only ever what was written, so this is the honest record.
   */
  const written = useRef<string | null>(null);

  const flush = useCallback(() => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    const to = pending.current;
    pending.current = null;
    if (to === null) return;
    lastWrite.current = Date.now();
    written.current = to;
    history.replaceState(history.state, "", to);
    setRoute(current());
  }, []);

  useEffect(() => () => {
    if (timer.current !== null) clearTimeout(timer.current);
  }, []);

  /** Push a new entry, remembering where we were on the one we are leaving. */
  const navigate = useCallback(
    (to: string) => {
      // Any coalesced query change belongs to the entry being left behind, so
      // it has to land before the push rather than after it.
      flush();
      stashScroll();
      written.current = to;
      history.pushState(null, "", to);
      setRoute(current());
    },
    [flush],
  );

  /**
   * Update the query. By default the current entry is replaced; with `push`
   * the change gets an entry of its own, so Back undoes it.
   *
   * Replacing is leading edge, then coalesced: a single change lands immediately, a drag
   * lands its final value one gap after the finger stops.
   */
  const replace = useCallback(
    (to: string, push = false) => {
      // Already there, and nothing queued that would overwrite it.
      if (pending.current === null && to === location.pathname + location.search) return;
      if (push) {
        // A finished change (D33): the entry being left keeps the old query, so
        // anything queued lands on it first, then the new query gets its own.
        // Pushes are rare (one per finished change), and a push is a history
        // write too, so it counts toward the gap like any other.
        flush();
        pending.current = null;
        stashScroll();
        lastWrite.current = Date.now();
        written.current = to;
        history.pushState(null, "", to);
        setRoute(current());
        return;
      }
      pending.current = to;
      if (timer.current !== null) return;
      const wait = HISTORY_MIN_GAP_MS - (Date.now() - lastWrite.current);
      if (wait <= 0) {
        flush();
        return;
      }
      timer.current = setTimeout(flush, wait);
    },
    [flush],
  );

  return { route, navigate, replace, written };
}

/** Scroll position stashed on the current history entry, if any. */
export function stashedScroll(): number | null {
  const s = history.state as HistoryState | null;
  return typeof s?.scrollY === "number" ? s.scrollY : null;
}

/**
 * Browsers restore scroll for same-document history navigation, but only after
 * they have something to scroll — and this list renders after an async fetch,
 * by which time the browser has given up. So it is done deliberately instead.
 */
export function disableBrowserScrollRestoration() {
  if ("scrollRestoration" in history) history.scrollRestoration = "manual";
}
